import Idea, {fieldVisible} from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {asArray} from '@/utils/arrayUtils'
import {NameResolver, effectiveCardinality} from '@/utils/propertyDefinition'
import {displayKind} from '@/utils/propertyDisplay'
import {isSingleLineMarkdown} from '@/utils/markdownExcerpt'

/**
 * Em que seções a ficha da instância se divide, e em que ordem. Com mais de uma, cada seção é uma
 * aba (ver a ficha).
 *
 * Mora fora do componente porque é decisão de DADO, não de desenho: o que vem antes do
 * quê, o que é tabela e o que é bloco de campos. A ficha só percorre a lista que sai daqui.
 *
 * **Um nível só** (decisão do usuário): a seção do item, as dos markdowns longos dele e uma por
 * relacionamento dele. O relacionamento de um relacionado não tem seção — é campo, um link, na
 * seção do relacionado. Já houve seções aninhadas ("Cliente · Cidade"), soltas e depois como
 * subabas: abas dentro de abas confundiam, e o que importava ver já está no link.
 */

export type InstanceSection = {
    /** Chave de v-for e valor da aba, estável: o alias do relacionamento, `#alias` do markdown. */
    key: string
    /** O nome da seção — o da aba: a coleção do item, o campo de markdown, o relacionamento. */
    path: string
    /** A coleção dos itens da seção, para quem precisa dela inteira (fluxo, colunas da tabela). */
    idea: Idea
    /** O item da seção; nas tabelas, o dono da lista. */
    instance: any
    /** O relacionamento que levou até aqui, ou null na seção do item e nas de markdown. */
    property: IdeaProperty | null
    /** Já na ordem de exibição: identificador primeiro. */
    properties: IdeaProperty[]
    /** Relacionamento múltiplo continua como tabela; o resto é bloco de campos. */
    table: boolean
    /**
     * O markdown que esta seção mostra inteiro, ou null. O longo (mais de uma linha, ou com título)
     * aparece nos campos linearizado, com o ⤢, e numa seção própria desenhado (decisão do usuário) —
     * ver `markdownSectionsOf`. Fica fora do `property`, que é o relacionamento e decide o resto da
     * seção (quadro, adicionar).
     */
    markdown: IdeaProperty | null
}

/** Como a ficha alcança a coleção de um relacionamento. */
export type IdeaResolver = (alias: string) => Idea | undefined

/** O valor da aba da própria instância: a chave da seção dela é vazia, e aba sem valor não se acha. */
export const OWN_TAB = '_item'

function valuesOf(instance: any, property: IdeaProperty): any[] {
    if (!instance) return []
    if (property.relationship) return asArray(instance._rels && instance._rels[property.alias])
    return asArray(instance[property.alias])
}

/**
 * Identificador primeiro, o resto na ordem de declaração da coleção.
 *
 * Estável dos dois lados: o filter preserva a ordem original dentro de cada grupo, então
 * reordenar propriedades no design continua se refletindo aqui.
 */
export function orderedProperties(properties: IdeaProperty[]): IdeaProperty[] {
    return [...properties.filter(p => p.identifier), ...properties.filter(p => !p.identifier)]
}

/**
 * Cardinalidade 1 — o critério que substituiu a antiga contagem de chaves devolvidas
 * pelo servidor.
 *
 * O `names` é obrigatório aqui, e não o resolvedor padrão do propertyDefinition: um
 * relacionamento ESPELHADO não guarda a própria cardinalidade, ela é o inverso da
 * exclusividade do outro lado, e achar o outro lado depende do resolvedor. Sem ele todo
 * reverso passaria por cardinalidade 1 — que é justamente o caso mais comum de lista.
 */
function isSingle(property: IdeaProperty, names: NameResolver): boolean {
    return !effectiveCardinality(property, names).multiple
}

/**
 * Uma instância tem algo a mostrar além do identificador — os mesmos campos que a aba dela mostra
 * (`fieldsOf`): os relacionamentos dela contam, quando vieram resolvidos (a leitura da ficha pede
 * `depth: 2`). Já não contaram: era do tempo em que eles viravam seções à parte, e uma pessoa cujos
 * campos preenchidos eram todos relacionamentos (área, sistemas) ficava sem aba (relato do usuário).
 *
 * A volta não conta: aponta para o item que a ficha mostra, e sempre tem valor.
 */
function hasFieldsBeyondIdentifier(idea: Idea, instance: any, cameFrom: IdeaProperty, fromIdea: string): boolean {
    return idea.properties
        .some(p => !p.identifier && !isWayBack(p, cameFrom, fromIdea) && valuesOf(instance, p).length)
}

/**
 * O relacionamento rende uma SEÇÃO, ou é só o link nos campos.
 *
 * Sem nada além do identificador, a seção repetiria o que o link já diz — o servidor
 * compõe o `_identifier` a partir das propriedades-chave, resolvendo inclusive os
 * relacionamentos que entram nele. Aí não vale a aba.
 *
 * Olha os VALORES, e não só o desenho da coleção: ela pode ter campos além da chave e
 * este item não tê-los preenchidos. Em relacionamento múltiplo basta um item com
 * detalhe — a tabela existe para o conjunto, não para cada linha.
 */
function hasDetail(property: IdeaProperty, owner: any, ownerIdea: Idea, ideas: IdeaResolver): boolean {
    const idea = ideas(property.relationship!.idea)
    if (!idea) return false
    return valuesOf(owner, property).some(item => hasFieldsBeyondIdentifier(idea, item, property, ownerIdea.alias))
}

/**
 * Os campos que um bloco desenha: todos, os relacionamentos inclusive — como link, com a régua do
 * item (decisão do usuário). Que um relacionamento tenha a sua aba não o tira daqui: aqui se vê com
 * quem o item se liga; lá, os detalhes.
 *
 * Sem valor ENTRA, com um traço (pedido do usuário): ficava de fora, e com ele sumia a marca do
 * que está vazio e não podia — o obrigatório que ficou em branco, a regra que pede um valor. A
 * ficha mostra o item inteiro, e vazio é uma resposta a ver.
 *
 * Nos campos de um RELACIONADO (`cameFrom`), duas exceções:
 * - a volta por onde se veio não entra: seria um link para o item que está sendo mostrado;
 * - o relacionamento dele só entra se veio resolvido. O servidor resolve o segundo nível apenas
 *   pelas cadeias do identificador (ver `lookupRels(idea, true)` no InstanceRepository); o resto
 *   chega como id, e na ficha sairia um traço — "vazio" — para o que tem valor.
 */
function fieldsOf(idea: Idea, instance: any, cameFrom: IdeaProperty | null = null, fromIdea = ''): IdeaProperty[] {
    return orderedProperties(idea.properties)
        .filter(p => !isWayBack(p, cameFrom, fromIdea))
        .filter(p => !cameFrom || !p.relationship || !!(instance && instance._rels && instance._rels[p.alias]))
}

/**
 * Markdown com algum valor de mais de uma linha (ver `isSingleLineMarkdown`): texto com estrutura —
 * lista, título, parágrafos —, que linearizado nos campos perde a forma. Ganha a seção dele, com o
 * texto desenhado.
 */
function isLongMarkdown(property: IdeaProperty, instance: any): boolean {
    return displayKind(property) === 'markdown'
        && valuesOf(instance, property).some(value => !isSingleLineMarkdown(value))
}

/**
 * As seções dos markdowns longos do item, na ordem da coleção, logo depois da seção dele (decisão
 * do usuário). Só os do item: os de um relacionado ficam nos campos da seção dele, linearizados.
 */
function markdownSectionsOf(idea: Idea, instance: any): InstanceSection[] {
    return orderedProperties(idea.properties)
        .filter(p => isLongMarkdown(p, instance))
        .map(p => ({
            key: `#${p.alias}`,
            path: p.name,
            idea,
            instance,
            property: null,
            properties: [],
            table: false,
            markdown: p
        }))
}

/**
 * A volta do caminho: a propriedade do outro lado que descreve a MESMA associação por
 * que acabamos de passar.
 *
 * Sem descartá-la, os campos do Cliente, na ficha do Pedido, trariam "Pedidos" — um link para
 * justamente o pedido de onde viemos.
 *
 * As duas condições importam. O alias sozinho casaria com outro relacionamento entre as
 * mesmas duas coleções, que é associação diferente; e o espelho tanto pode estar declarado de
 * um lado (`reverse` aponta para cá) quanto do outro.
 */
function isWayBack(property: IdeaProperty, cameFrom: IdeaProperty | null, fromIdea: string): boolean {
    if (!cameFrom || !property.relationship || !cameFrom.relationship) return false
    if (property.relationship.idea !== fromIdea) return false
    return property.relationship.reverse === cameFrom.alias
        || cameFrom.relationship.reverse === property.alias
}

/**
 * A seção de um relacionamento do item, ou null quando ele não tem detalhe — aí fica só o link,
 * nos campos.
 *
 * O simples é um bloco com os campos do relacionado; o múltiplo, a tabela. Os espelhados (reversos)
 * entram como qualquer outro: quem decide entre tabela e bloco é a cardinalidade efetiva, não o
 * lado em que foram declarados.
 *
 * O múltiplo tem aba também sem detalhe (pedido do usuário: as Subtasks vazias, com o aviso):
 * - quando quem olha pode adicionar (`addable`) — vazio ou não, para a aba não sumir ao entrar o
 *   primeiro item sem detalhe;
 * - vazio, quando a coleção tem campos além do nome: a aba em que eles apareceriam.
 */
function sectionFor(
    property: IdeaProperty, owner: any, ownerIdea: Idea, ideas: IdeaResolver, names: NameResolver,
    addable: (property: IdeaProperty) => boolean
): InstanceSection | null {
    const idea = ideas(property.relationship!.idea)
    if (!idea) return null
    const table = !isSingle(property, names)
    const values = valuesOf(owner, property)
    const listed = table && (addable(property) || (!values.length && hasFieldsBeyondName(idea, property, ownerIdea.alias)))
    if (!listed && !hasDetail(property, owner, ownerIdea, ideas)) return null
    return {
        key: property.alias,
        path: property.name,
        idea,
        //Na tabela quem manda é a lista inteira, e ela se alcança pelo dono
        instance: table ? owner : values[0],
        property,
        properties: table ? orderedProperties(idea.properties) : fieldsOf(idea, values[0], property, ownerIdea.alias),
        table,
        markdown: null
    }
}

/** A coleção tem campo além do nome — e da volta por onde se veio, que não aparece na tabela. */
function hasFieldsBeyondName(idea: Idea, cameFrom: IdeaProperty, fromIdea: string): boolean {
    return idea.properties.some(p => !p.identifier && !isWayBack(p, cameFrom, fromIdea))
}

/**
 * A ficha inteira: a seção da própria instância, as dos markdowns longos dela (decisão do usuário)
 * e uma por relacionamento com valor — ou múltiplo, vazio, que mereça a aba (`sectionFor`) —, na ordem
 * de declaração da coleção.
 *
 * `addable`: quem olha pode adicionar ao relacionamento — quem sabe é a ficha (`relationshipEditing`).
 * O vazio que quem olha não vê não tem aba: o servidor o manda vazio, e a aba diria "não há itens".
 */
export function instanceSections(
    idea: Idea, instance: any, ideas: IdeaResolver, names: NameResolver,
    addable: (property: IdeaProperty) => boolean = () => false
): InstanceSection[] {
    const own: InstanceSection = {
        key: '',
        //A seção da própria instância se chama pela coleção; o cabeçalho da página já
        //diz qual item é, então repetir o identificador aqui não acrescentaria nada
        path: idea.name,
        idea,
        instance,
        property: null,
        properties: fieldsOf(idea, instance),
        table: false,
        markdown: null
    }
    const relationships = idea.properties
        .filter(p => p.relationship && (valuesOf(instance, p).length || fieldVisible(instance, p)))
        .map(p => sectionFor(p, instance, idea, ideas, names, addable))
        .filter((section): section is InstanceSection => !!section)
    return [own, ...markdownSectionsOf(idea, instance), ...relationships]
}
